import { ConsentNotice } from "@/components/ConsentNotice";
import { useIntake } from "@/features/intake/IntakeContext";
import { toChoiceOption } from "@/types/offer";
import type { IntakeStep, ReviewStep as ReviewStepConfig } from "@/types/offer";
import type { StepRenderProps } from "./types";

function formatAnswer(step: IntakeStep, value: unknown): string {
  switch (step.type) {
    case "singleChoice": {
      const match = step.options.map(toChoiceOption).find((o) => o.value === value);
      return match?.label ?? (typeof value === "string" ? value : "-");
    }
    case "multiChoice": {
      const arr = Array.isArray(value) ? (value as string[]) : [];
      if (arr.length === 0) return "-";
      const labels = step.options.map(toChoiceOption);
      return arr.map((v) => labels.find((o) => o.value === v)?.label ?? v).join(", ");
    }
    default:
      return value != null && value !== "" ? String(value) : "-";
  }
}

export function ReviewStep(_props: StepRenderProps<ReviewStepConfig>) {
  const { offer, answers, uploadItems, goTo, submit, phase } = useIntake();
  const step = _props.step;

  const reviewable = offer.steps
    .map((s, i) => ({ s, i }))
    .filter(({ s }) => s.type !== "statement" && s.type !== "review");

  return (
    <div>
      <dl className="divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white">
        {reviewable.map(({ s, i }) => (
          <div key={s.id} className="flex items-start justify-between gap-4 px-4 py-3">
            <div className="min-w-0">
              <dt className="text-sm font-medium text-ink-muted">{s.title}</dt>
              <dd className="mt-0.5 break-words text-base text-ink">
                {s.type === "fileUpload"
                  ? uploadItems.filter((u) => u.status === "done").map((u) => u.fileName).join(", ") || "-"
                  : formatAnswer(s, answers[s.id])}
              </dd>
            </div>
            <button
              type="button"
              className="r1-btn-ghost shrink-0 px-2 py-1 text-sm"
              onClick={() => goTo(i)}
            >
              Edit
            </button>
          </div>
        ))}
      </dl>

      <ConsentNotice
        deliverable={offer.pipeline === "sop-coverage" ? "coverage report" : "obligations export"}
      />

      <button
        type="button"
        className="r1-btn-primary mt-6 w-full sm:w-auto"
        onClick={() => void submit()}
        disabled={phase === "submitting"}
      >
        {phase === "submitting" ? "Submitting…" : step.submitLabel ?? "Submit"}
      </button>
    </div>
  );
}
